import React, { useState, useMemo } from 'react';
import {
  Users,
  Search,
  Filter,
  Plus,
  Compass,
  Briefcase,
  Award,
  Calendar,
  MapPin,
  Clock,
  Zap,
  ChevronRight,
  ChevronDown,
  UserCheck,
  Tag,
  AlertCircle
} from 'lucide-react';
import { PracticeClient, PracticeCase } from '../../types/practiceManagement';
import { Language, CompleteKundali } from '../../types/kundali';
import { ClientProfile } from './ClientProfile';

interface ClientManagementHubProps {
  clients: PracticeClient[];
  lang: Language;
  onOpenKundali: (client: PracticeClient) => void;
  onNewClient: () => void;
  onNewCaseForClient: (client: PracticeClient) => void;
  onNewConsultationForClient: (client: PracticeClient) => void;
  onGenerateReportForClient: (client: PracticeClient) => void;
  onOpenCaseWorkspace: (caseItem: PracticeCase) => void;
}

export const ClientManagementHub: React.FC<ClientManagementHubProps> = ({
  clients,
  lang,
  onOpenKundali,
  onNewClient,
  onNewCaseForClient,
  onNewConsultationForClient,
  onGenerateReportForClient,
  onOpenCaseWorkspace
}) => {
  const isHi = lang === 'hi';
  const [selectedClient, setSelectedClient] = useState<PracticeClient | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [tagFilter, setTagFilter] = useState<string>('ALL');

  // Filtered client list
  const filteredClients = useMemo(() => {
    return clients.filter((c) => {
      const matchSearch =
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.referenceId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.placeOfBirth.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.lagnaSign.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.moonSign.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.phone && c.phone.includes(searchTerm)) ||
        (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchStatus =
        statusFilter === 'ALL' ||
        (statusFilter === 'ACTIVE' && c.status === 'ACTIVE') ||
        (statusFilter === 'FOLLOW_UP_DUE' && c.status === 'FOLLOW_UP_DUE') ||
        (statusFilter === 'TIMING_ALERT' && c.hasUpcomingTimingAlert);

      const matchTag = tagFilter === 'ALL' || (c.tags && c.tags.includes(tagFilter));

      return matchSearch && matchStatus && matchTag;
    });
  }, [clients, searchTerm, statusFilter, tagFilter]);

  // If a client is selected, show their full ClientProfile
  if (selectedClient) {
    return (
      <ClientProfile
        client={selectedClient}
        lang={lang}
        onBack={() => setSelectedClient(null)}
        onOpenKundali={onOpenKundali}
        onNewCaseForClient={onNewCaseForClient}
        onNewConsultationForClient={onNewConsultationForClient}
        onGenerateReportForClient={onGenerateReportForClient}
        onOpenCaseWorkspace={onOpenCaseWorkspace}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Directory Control Bar */}
      <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[260px]">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-astro-muted absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={
                isHi
                  ? 'क्लाइंट नाम, ID, जन्म स्थान या राशि से खोजें...'
                  : 'Search by client name, ID, location, lagna or phone...'
              }
              className="w-full pl-9 pr-3 py-2 text-xs bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-xs bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            <option value="ALL">{isHi ? 'सभी स्थिति' : 'All Clients'}</option>
            <option value="ACTIVE">{isHi ? 'सक्रिय क्लाइंट' : 'Active Only'}</option>
            <option value="FOLLOW_UP_DUE">{isHi ? 'फॉलो-अप लंबित' : 'Follow-Up Due'}</option>
            <option value="TIMING_ALERT">{isHi ? 'आगामी गोचर अलर्ट' : 'Timing Alerts'}</option>
          </select>
        </div>

        <button
          onClick={onNewClient}
          className="min-h-[38px] px-3.5 py-1.5 rounded-lg bg-astro-secondary-surface0 hover:bg-astro-secondary text-white text-xs font-bold flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
        >
          <Plus className="w-4 h-4" />
          <span>{isHi ? '+ नया क्लाइंट' : '+ New Client'}</span>
        </button>
      </div>

      {/* Clients Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
        {filteredClients.map((client) => (
          <div
            key={client.id}
            id={`client-card-${client.id}`}
            className="group bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs hover:shadow-md transition-all hover:border-amber-400/50 flex flex-col justify-between gap-3"
          >
            {/* Header */}
            <div>
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-mono font-bold text-[#52635F] dark:text-amber-400 bg-astro-secondary-surface dark:bg-amber-950/40 px-2 py-0.2 rounded border border-[#E2DED5] dark:border-amber-800">
                      {client.referenceId}
                    </span>
                    {client.hasUpcomingTimingAlert && (
                      <span
                        title="Upcoming Timing Confluence"
                        className="p-1 rounded bg-astro-secondary-surface0/15 text-amber-600 dark:text-amber-400"
                      >
                        <Zap className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                  <h3
                    onClick={() => setSelectedClient(client)}
                    className="font-bold text-astro-text dark:text-astro-muted text-sm hover:text-amber-600 dark:hover:text-amber-400 cursor-pointer pt-0.5"
                  >
                    {client.name}
                  </h3>
                </div>

                <span
                  className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    client.status === 'ACTIVE'
                      ? 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300'
                      : 'bg-astro-secondary-surface0/15 text-astro-secondary dark:text-amber-300'
                  }`}
                >
                  {client.status}
                </span>
              </div>

              {/* Coordinates */}
              <div className="space-y-1 text-xs text-astro-secondary-text dark:text-astro-muted">
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-3 h-3 text-astro-muted shrink-0" />
                  <span>
                    DOB: {client.dateOfBirth} ({client.ageYears}y) • {client.timeOfBirth}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 truncate">
                  <MapPin className="w-3 h-3 text-astro-muted shrink-0" />
                  <span className="truncate">{client.placeOfBirth}</span>
                </div>
              </div>

              {/* Astrology facts pill strip */}
              <div className="flex flex-wrap gap-1.5 pt-2">
                <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-astro-secondary-surface dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted">
                  Lagna: {client.lagnaSign.split(' ')[0]}
                </span>
                <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-astro-secondary-surface dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted">
                  Moon: {client.moonSign.split(' ')[0]}
                </span>
                <span className="text-[10px] font-medium px-1.5 py-0.2 rounded bg-astro-secondary-surface dark:bg-amber-950/40 text-astro-secondary dark:text-amber-300">
                  {client.activeDasha.split(' ')[0]}
                </span>
              </div>
            </div>

            {/* Bottom Actions */}
            <div className="flex items-center gap-2 pt-2 border-t border-astro-border dark:border-astro-strong-border">
              <button
                onClick={() => setSelectedClient(client)}
                className="flex-1 min-h-[34px] px-2.5 py-1.5 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary hover:bg-astro-card dark:hover:bg-astro-secondary text-astro-text dark:text-astro-muted font-bold text-xs flex items-center justify-center gap-1 transition-colors cursor-pointer"
              >
                <span>{isHi ? 'प्रोफ़ाइल' : 'Profile'}</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>

              <button
                onClick={() => onOpenKundali(client)}
                title={isHi ? 'कुंडली खोलें' : 'Open Kundali'}
                className="p-2 rounded-lg bg-astro-secondary-surface0 hover:bg-astro-secondary text-white transition-colors cursor-pointer"
              >
                <Compass className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredClients.length === 0 && (
        <div className="p-12 text-center bg-white dark:bg-slate-850 rounded-xl border border-astro-border/80 dark:border-astro-strong-border/80 space-y-3">
          <Users className="w-8 h-8 text-astro-muted mx-auto" />
          <div className="space-y-1">
            <h4 className="text-sm font-bold text-astro-text dark:text-astro-muted">
              {isHi ? 'कोई क्लाइंट नहीं मिला' : 'No clients found'}
            </h4>
            <p className="text-xs text-astro-muted">
              {isHi
                ? 'अपने खोज मानदंड बदलें या नया क्लाइंट जोड़ें।'
                : 'Try adjusting your search filters or create a new client intake.'}
            </p>
          </div>
          <button
            onClick={onNewClient}
            className="px-4 py-2 rounded-lg bg-astro-secondary-surface0 text-white font-bold text-xs cursor-pointer shadow-xs"
          >
            + Create New Client
          </button>
        </div>
      )}
    </div>
  );
};
